import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  MapPin, Phone, Clock, Shield, Star, CheckCircle, 
  Wrench, Award, Users, ArrowRight, ChevronDown,
  Zap, Lightbulb, Plug, AlertTriangle, Home
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// Elektrik hizmetleri
const hizmetler = [
  { icon: Zap, title: 'Elektrik Arızası', desc: 'Sigorta atması, kısa devre, elektrik kesintisi', price: '₺400\'den başlayan' },
  { icon: Lightbulb, title: 'Aydınlatma', desc: 'Avize montajı, spot lamba, LED dönüşüm', price: '₺200\'den başlayan' },
  { icon: Plug, title: 'Priz & Anahtar', desc: 'Priz değişimi, anahtar tamiri, topraklama', price: '₺150\'den başlayan' },
  { icon: Home, title: 'Tesisat Yenileme', desc: 'Komple elektrik tesisatı döşeme', price: 'Keşfe bağlı' },
  { icon: Shield, title: 'Sigorta Panosu', desc: 'Pano değişimi, otomatik sigorta montajı', price: '₺500\'den başlayan' },
  { icon: AlertTriangle, title: 'Acil Servis', desc: '7/24 acil elektrik arıza müdahalesi', price: '₺500\'den başlayan' },
];

// Yaygın sorunlar
const yaygınSorunlar = [
  {
    sorun: 'Sigorta Sürekli Atıyor',
    sebep: 'Aşırı yüklenme, kısa devre veya arızalı cihaz',
    cozum: 'Hattı kontrol ettirin, arızalı cihazı tespit edin. Kesinlikle tel ile bypass yapmayın!',
    tehlike: 'high'
  },
  {
    sorun: 'Prizden Kıvılcım Çıkıyor',
    sebep: 'Gevşek bağlantı veya hasarlı priz',
    cozum: 'Derhal kullanmayı bırakın ve elektrikçi çağırın. Yangın riski!',
    tehlike: 'critical'
  },
  {
    sorun: 'Işıklar Titriyor',
    sebep: 'Gevşek bağlantı, voltaj dalgalanması veya ampul sorunu',
    cozum: 'Farklı ampul deneyin. Tüm evde oluyorsa tesisat kontrolü gerekli.',
    tehlike: 'medium'
  },
  {
    sorun: 'Bazı Prizler Çalışmıyor',
    sebep: 'Kaçak akım rölesi atmış veya hat arızası',
    cozum: 'Kaçak akım şalterini kontrol edin. Atmamışsa hat arızası var.',
    tehlike: 'medium'
  },
  {
    sorun: 'Elektrik Çarpması Hissi',
    sebep: 'Topraklama eksikliği veya kaçak akım',
    cozum: 'ACİL! Cihazı fişten çekin, elektrikçi çağırın.',
    tehlike: 'critical'
  },
  {
    sorun: 'Kablo Kokusu',
    sebep: 'Aşırı ısınma, kısa devre başlangıcı',
    cozum: 'Ana sigortayı kapatın ve derhal elektrikçi çağırın!',
    tehlike: 'critical'
  },
];

// Güvenlik ipuçları
const guvenlikIpuclari = [
  'Elektrik işlerini asla kendiniz yapmaya çalışmayın',
  'Sigortaları tel veya folyo ile bypass yapmayın',
  'Islak elle elektrikli cihazlara dokunmayın',
  'Çocukların ulaşamayacağı priz kapakları kullanın',
  '10 yıldan eski tesisatı mutlaka kontrol ettirin',
  'Yangın söndürücü bulundurun ve yerini bilin',
];

// SSS
const sikSorulanSorular = [
  {
    soru: 'Elektrikçi ücreti ne kadar?',
    cevap: 'Basit işlemler (priz değişimi) 150-300 TL, arıza tespiti 400-600 TL, kapsamlı işler keşfe göre fiyatlandırılır. Acil servis %50 ek ücret içerir.'
  },
  {
    soru: 'Kaçak akım rölesi nedir?',
    cevap: 'Elektrik kaçağı algılayınca devreyi kesen güvenlik cihazıdır. Düzenli test edilmeli (test butonuna basarak). Atmıyorsa arızalıdır.'
  },
  {
    soru: 'Ev elektrik tesisatı kaç yılda bir yenilenmeli?',
    cevap: '20-25 yıllık tesisatlar mutlaka kontrol edilmeli. Alüminyum kablolu eski evlerde bakır kabloya geçiş önerilir.'
  },
  {
    soru: 'Topraklama neden önemli?',
    cevap: 'Topraklama, cihazlardaki kaçak akımı güvenli şekilde toprağa iletir. Eksik topraklama elektrik çarpması ve yangın riski oluşturur.'
  },
  {
    soru: 'Acil durumda ne yapmalıyım?',
    cevap: 'Ana sigortayı kapatın, elektrikli cihazları fişten çekin, yangın varsa itfaiyeyi arayın. Ardından profesyonel elektrikçi çağırın.'
  },
];

const ElektrikServisi = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "HizmetGeliyo Elektrik Servisi",
    "description": "Profesyonel elektrik tesisatı servisi. Arıza, aydınlatma, priz montajı, sigorta panosu. 7/24 acil servis.",
    "url": "https://hizmetgeliyo.com/elektrik-servisi",
    "telephone": "+90-850-XXX-XXXX",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "TR"
    },
    "openingHours": "Mo-Su 00:00-23:59",
    "priceRange": "₺₺",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "670"
    }
  };

  const getTehlikeColor = (tehlike) => {
    switch(tehlike) {
      case 'critical': return 'bg-red-100 border-red-300 text-red-800';
      case 'high': return 'bg-orange-100 border-orange-300 text-orange-800';
      default: return 'bg-yellow-100 border-yellow-300 text-yellow-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50/30">
      <Helmet>
        <title>Elektrik Servisi - Elektrikçi, Tesisat, Arıza | HizmetGeliyo</title>
        <meta name="description" content="Profesyonel elektrikçi hizmeti: arıza, sigorta, priz, aydınlatma, tesisat yenileme. 7/24 acil servis. Lisanslı ustalar, garantili iş." />
        <meta name="keywords" content="elektrikçi, elektrik arızası, sigorta atıyor, priz tamiri, aydınlatma montajı, elektrik tesisatı, acil elektrikçi, kaçak akım" />
        <link rel="canonical" href="https://hizmetgeliyo.com/elektrik-servisi" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <Navbar />

      {/* Hero */}
      <section className="relative pt-20 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500 via-orange-500 to-red-500"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-yellow-300 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Zap className="w-5 h-5 text-white" />
              <span className="text-white font-medium">7/24 Acil Elektrik Servisi</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Profesyonel <span className="text-yellow-200">Elektrik</span> Servisi
            </h1>
            
            <p className="text-xl text-orange-100 max-w-3xl mx-auto mb-8">
              Arıza, aydınlatma, priz montajı, tesisat yenileme. Lisanslı elektrikçiler.
              <strong className="text-white"> Güvenliğiniz bizim önceliğimiz.</strong>
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button 
                onClick={() => navigate('/talep/elektrik')}
                className="bg-white text-orange-600 hover:bg-orange-50 px-8 py-4 rounded-full text-lg font-semibold shadow-xl"
              >
                <Zap className="w-5 h-5 mr-2" />
                Elektrikçi Çağır
              </Button>
              <Button 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-full text-lg"
              >
                <Phone className="w-5 h-5 mr-2" />
                Acil: 7/24
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-6 text-white/90">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-yellow-300" />
                <span>Lisanslı Ustalar</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-yellow-300" />
                <span>Sigortalı İş</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-300" />
                <span>4.9/5 Puan</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-yellow-300" />
                <span>7/24 Hizmet</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Uyarı Bandı */}
      <section className="bg-red-600 py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-2 text-white">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-medium">⚠️ Elektrik işlerini asla kendiniz yapmaya çalışmayın! Hayati tehlike içerir.</span>
          </div>
        </div>
      </section>

      {/* Hizmetler */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Elektrik Hizmetlerimiz</h2>
            <p className="text-gray-600">Tüm elektrik ihtiyaçlarınız için profesyonel çözümler</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {hizmetler.map((hizmet, idx) => (
              <div 
                key={idx}
                className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 hover:shadow-xl transition-all border border-amber-100 cursor-pointer"
                onClick={() => navigate('/talep/elektrik')}
              >
                <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center mb-4">
                  <hizmet.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{hizmet.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{hizmet.desc}</p>
                <p className="text-orange-600 font-semibold">{hizmet.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Yaygın Sorunlar */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Yaygın Elektrik Sorunları</h2>
            <p className="text-gray-600">Bu belirtileri görüyorsanız profesyonel yardım alın</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {yaygınSorunlar.map((item, idx) => (
              <div key={idx} className={`rounded-xl p-6 border-2 ${getTehlikeColor(item.tehlike)}`}>
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-5 h-5" />
                  <h3 className="font-bold text-lg">{item.sorun}</h3>
                </div>
                <p className="text-sm mb-2"><strong>Olası Sebep:</strong> {item.sebep}</p>
                <p className="text-sm"><strong>Yapılması Gereken:</strong> {item.cozum}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Button 
              onClick={() => navigate('/talep/elektrik')}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <Zap className="w-5 h-5 mr-2" />
              Elektrikçi Çağır
            </Button>
          </div>
        </div>
      </section>

      {/* Güvenlik İpuçları */}
      <section className="py-16 bg-gradient-to-r from-gray-900 to-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Elektrik Güvenliği İpuçları</h2>
            <p className="text-gray-300">Güvenliğiniz için bu kurallara uyun</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {guvenlikIpuclari.map((ipucu, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-white/10 rounded-xl p-4">
                <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                <span className="text-white">{ipucu}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SSS */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Sıkça Sorulan Sorular</h2>
          </div>

          <div className="space-y-4">
            {sikSorulanSorular.map((faq, idx) => (
              <div key={idx} className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full px-6 py-4 flex items-center justify-between bg-white hover:bg-gray-50"
                >
                  <span className="font-semibold text-gray-900 text-left">{faq.soru}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${openFaq === idx ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === idx && (
                  <div className="px-6 py-4 bg-gray-50 border-t">
                    <p className="text-gray-600">{faq.cevap}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-amber-500 to-orange-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Elektrik Sorununuz mu Var?
          </h2>
          <p className="text-orange-100 text-lg mb-8">
            Güvenliğinizi riske atmayın. Lisanslı elektrikçilerimiz 7/24 hizmetinizde.
          </p>
          <Button 
            onClick={() => navigate('/talep/elektrik')}
            className="bg-white text-orange-600 hover:bg-orange-50 px-8 py-4 rounded-full text-lg font-semibold"
          >
            Elektrikçi Çağır <ArrowRight className="w-5 h-5 ml-2 inline" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ElektrikServisi;
